import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/providers/auth-provider";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Plus, 
  Loader2, 
  Save, 
  Trash, 
  CheckCircle2, 
  AlertCircle, 
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  Dialog, 
  DialogClose, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";

const subscriptionPlanSchema = z.object({
  name: z.string().min(3, { message: "Name must be at least 3 characters" }),
  description: z.string().min(5, { message: "Description must be at least 5 characters" }),
  price: z.coerce.number().min(1, { message: "Price must be at least 1" }),
  features: z.union([z.string(), z.array(z.string())]).optional().transform(val => {
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch (e) {
        return [];
      }
    }
    return val;
  }),
  stripePriceId: z.string().optional(),
  isActive: z.boolean().default(true),
});

type SubscriptionPlanFormValues = z.infer<typeof subscriptionPlanSchema>;

// Feature list component for display and editing
function FeatureList({ features, onFeatureUpdate }: { 
  features: string[], 
  onFeatureUpdate?: (features: string[]) => void 
}) {
  const [newFeature, setNewFeature] = useState("");
  const [editingFeatures, setEditingFeatures] = useState<string[]>(features);
  
  useEffect(() => {
    setEditingFeatures(features);
  }, [features]);
  
  const addFeature = () => {
    if (!newFeature) return;
    const updated = [...editingFeatures, newFeature];
    setEditingFeatures(updated);
    setNewFeature("");
    if (onFeatureUpdate) onFeatureUpdate(updated);
  };
  
  const removeFeature = (index: number) => {
    const updated = editingFeatures.filter((_, i) => i !== index);
    setEditingFeatures(updated);
    if (onFeatureUpdate) onFeatureUpdate(updated);
  };
  
  return (
    <div className="space-y-4">
      {onFeatureUpdate && (
        <div className="flex gap-2">
          <Input
            placeholder="Add a new feature..."
            value={newFeature}
            onChange={(e) => setNewFeature(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addFeature();
              }
            }}
          />
          <Button 
            type="button" 
            variant="secondary" 
            onClick={addFeature} 
            size="icon"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      <div className="space-y-2">
        {editingFeatures.map((feature, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="flex-1 text-sm">
              <CheckCircle2 className="inline-block h-3.5 w-3.5 text-primary mr-2" />
              {feature}
            </div>
            {onFeatureUpdate && (
              <Button 
                type="button" 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6" 
                onClick={() => removeFeature(index)}
              >
                <Trash className="h-3.5 w-3.5 text-destructive" />
              </Button>
            )}
          </div>
        ))}
        {editingFeatures.length === 0 && (
          <div className="text-muted-foreground text-sm italic">
            No features added yet
          </div>
        )}
      </div>
    </div>
  );
}

export default function ManageSubscriptionPlansPage() {
  const { user, isLoading: authLoading } = useAuth();
  
  // Show loading while auth is being determined
  if (authLoading) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </div>
      </Layout>
    );
  }
  
  if (!user) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </div>
      </Layout>
    );
  }
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [addingNewPlan, setAddingNewPlan] = useState(false);
  const [editingPlan, setEditingPlan] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("active");
  const [_, navigate] = useLocation();
  
  // Check if user is admin (demo user gets admin access in development)
  const isAdmin = user && (user.username === "admin" || user.email === "hello@zentrobooks.com" || user.username === "demouser");
  
  // Debug logging (remove in production)
  console.log("ManageSubscriptionPlansPage - User:", user);
  console.log("ManageSubscriptionPlansPage - isAdmin:", isAdmin);
  
  // Fetch subscription plans
  const { data: plans = [], isLoading: plansLoading, isError, error } = useQuery({
    queryKey: ["/api/subscription-plans/all"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/subscription-plans/all");
      return res.json();
    }
  });
  
  // Handle query errors
  useEffect(() => {
    if (isError && error) {
      toast({
        title: "Error",
        description: `Failed to fetch subscription plans: ${(error as Error).message}`,
        variant: "destructive",
      });
    }
  }, [isError, error, toast]);
  
  // Filtered plans based on active tab
  const filteredPlans = plans.filter((plan: any) => {
    if (activeTab === "active") return plan.isActive;
    return !plan.isActive;
  });
  
  const createPlanMutation = useMutation({
    mutationFn: async (planData: SubscriptionPlanFormValues) => {
      // features is already transformed by zod schema, no need to parse again
      const response = await apiRequest("POST", "/api/subscription-plans", planData);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Subscription plan created successfully!",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans/all"] });
      setAddingNewPlan(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to create plan: ${error.message}`,
        variant: "destructive",
      });
    },
  });
  
  const updatePlanMutation = useMutation({
    mutationFn: async ({ id, planData }: { id: number; planData: Partial<SubscriptionPlanFormValues> }) => {
      // features is already transformed by zod schema, no need to parse again
      const response = await apiRequest("PATCH", `/api/subscription-plans/${id}`, planData);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Subscription plan updated successfully!",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans/all"] });
      setEditingPlan(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to update plan: ${error.message}`,
        variant: "destructive",
      });
    },
  });
  
  const togglePlanStatusMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      const response = await apiRequest("PATCH", `/api/subscription-plans/${id}`, {
        isActive,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Plan status updated successfully!",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans/all"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to update plan status: ${error.message}`,
        variant: "destructive",
      });
    },
  });
  
  // Check if user is admin
  if (!user || user.username !== "admin") {
    return (
      <Layout>
        <div className="container max-w-screen-lg py-12">
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
            <p className="text-muted-foreground">
              You don't have permission to access this page. This page is restricted to admin users only.
            </p>
          </div>
        </div>
      </Layout>
    );
  }
  
  // Form for adding new plans
  const NewPlanForm = () => {
    const [features, setFeatures] = useState<string[]>([]);
    
    const form = useForm<SubscriptionPlanFormValues>({
      resolver: zodResolver(subscriptionPlanSchema),
      defaultValues: {
        name: "",
        description: "",
        price: 9.99,
        features: JSON.stringify([]),
        stripePriceId: "",
        isActive: true,
      },
    });
    
    // Update features string when features change
    const updateFeatures = (newFeatures: string[]) => {
      setFeatures(newFeatures);
      form.setValue("features", JSON.stringify(newFeatures));
    };
    
    const onSubmit = (values: SubscriptionPlanFormValues) => {
      createPlanMutation.mutate(values);
    };
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Create New Subscription Plan</CardTitle>
          <CardDescription>Add a new subscription plan to offer to your customers</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Plan Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Basic Plan" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Monthly Price ($)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="9.99" 
                          step="0.01" 
                          min="0" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Describe the features and benefits of this plan..." 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="stripePriceId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stripe Price ID (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="price_1234567890" 
                        {...field} 
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormDescription>
                      Enter the Stripe Price ID to link this plan with Stripe for payment processing
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active Plan</FormLabel>
                      <FormDescription>
                        Make this plan available to users immediately
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="features"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Features</FormLabel>
                    <FormControl>
                      <input 
                        type="hidden" 
                        {...field} 
                        value={JSON.stringify(features)}
                      />
                    </FormControl>
                    <FeatureList 
                      features={features} 
                      onFeatureUpdate={updateFeatures} 
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAddingNewPlan(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createPlanMutation.isPending}
                >
                  {createPlanMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Create Plan
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    );
  };
  
  // Form for editing existing plans
  const EditPlanForm = ({ plan }: { plan: any }) => {
    const [features, setFeatures] = useState<string[]>(plan.features || []);
    
    const form = useForm<SubscriptionPlanFormValues>({
      resolver: zodResolver(subscriptionPlanSchema),
      defaultValues: {
        name: plan.name,
        description: plan.description || "",
        price: plan.price,
        features: JSON.stringify(plan.features || []),
        stripePriceId: plan.stripePriceId || "",
        isActive: plan.isActive,
      },
    });
    
    // Update features string when features change
    const updateFeatures = (newFeatures: string[]) => {
      setFeatures(newFeatures);
      form.setValue("features", JSON.stringify(newFeatures));
    };
    
    const onSubmit = (values: SubscriptionPlanFormValues) => {
      updatePlanMutation.mutate({ id: plan.id, planData: values });
    };
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Edit Subscription Plan</CardTitle>
          <CardDescription>Update the details of this subscription plan</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Plan Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Basic Plan" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Monthly Price ($)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="9.99" 
                          step="0.01" 
                          min="0" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Describe the features and benefits of this plan..." 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="stripePriceId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stripe Price ID (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="price_1234567890" 
                        {...field} 
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormDescription>
                      Enter the Stripe Price ID to link this plan with Stripe for payment processing
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active Plan</FormLabel>
                      <FormDescription>
                        Make this plan available to users immediately
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="features"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Features</FormLabel>
                    <FormControl>
                      <input 
                        type="hidden" 
                        {...field} 
                        value={JSON.stringify(features)}
                      />
                    </FormControl>
                    <FeatureList 
                      features={features} 
                      onFeatureUpdate={updateFeatures} 
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingPlan(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updatePlanMutation.isPending}
                >
                  {updatePlanMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    );
  };
  
  if (plansLoading) {
    return (
      <Layout>
        <div className="container max-w-screen-lg py-8 flex justify-center items-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="container max-w-screen-lg py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Subscription Plans</h1>
            <p className="text-muted-foreground">
              Manage the subscription plans available to your customers
            </p>
          </div>
          
          {!addingNewPlan && !editingPlan && (
            <Button onClick={() => setAddingNewPlan(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Plan
            </Button>
          )}
        </div>
      
      {addingNewPlan ? (
        <NewPlanForm />
      ) : editingPlan !== null ? (
        <EditPlanForm plan={plans.find((p: any) => p.id === editingPlan)} />
      ) : (
        <>
          <Tabs defaultValue="active" value={activeTab} onValueChange={setActiveTab}>
            <div className="flex justify-between items-center mb-4">
              <TabsList>
                <TabsTrigger value="active">Active Plans</TabsTrigger>
                <TabsTrigger value="inactive">Inactive Plans</TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="active" className="mt-0">
              {filteredPlans.length === 0 ? (
                <div className="text-center py-12 border rounded-lg">
                  <p className="text-muted-foreground">No active plans found</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredPlans.map((plan: any) => (
                    <Card key={plan.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle>{plan.name}</CardTitle>
                            <div className="mt-1">
                              <span className="text-2xl font-bold">${Number(plan.price).toFixed(2)}</span>
                              <span className="text-muted-foreground text-sm"> / month</span>
                            </div>
                          </div>
                          <Badge>{plan.isActive ? "Active" : "Inactive"}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                        <div className="mt-4">
                          <h4 className="font-medium mb-2">Features</h4>
                          <FeatureList features={plan.features || []} />
                        </div>
                      </CardContent>
                      <CardFooter className="flex justify-between">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => togglePlanStatusMutation.mutate({ 
                            id: plan.id, 
                            isActive: !plan.isActive 
                          })}
                          disabled={togglePlanStatusMutation.isPending}
                        >
                          {plan.isActive ? (
                            <>
                              <ToggleLeft className="mr-2 h-4 w-4" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <ToggleRight className="mr-2 h-4 w-4" />
                              Activate
                            </>
                          )}
                        </Button>
                        <Button 
                          variant="secondary" 
                          size="sm"
                          onClick={() => setEditingPlan(plan.id)}
                        >
                          Edit Plan
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="inactive" className="mt-0">
              {filteredPlans.length === 0 ? (
                <div className="text-center py-12 border rounded-lg">
                  <p className="text-muted-foreground">No inactive plans found</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredPlans.map((plan: any) => (
                    <Card key={plan.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle>{plan.name}</CardTitle>
                            <div className="mt-1">
                              <span className="text-2xl font-bold">${Number(plan.price).toFixed(2)}</span>
                              <span className="text-muted-foreground text-sm"> / month</span>
                            </div>
                          </div>
                          <Badge variant="outline">{plan.isActive ? "Active" : "Inactive"}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                        <div className="mt-4">
                          <h4 className="font-medium mb-2">Features</h4>
                          <FeatureList features={plan.features || []} />
                        </div>
                      </CardContent>
                      <CardFooter className="flex justify-between">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => togglePlanStatusMutation.mutate({ 
                            id: plan.id, 
                            isActive: !plan.isActive 
                          })}
                          disabled={togglePlanStatusMutation.isPending}
                        >
                          {plan.isActive ? (
                            <>
                              <ToggleLeft className="mr-2 h-4 w-4" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <ToggleRight className="mr-2 h-4 w-4" />
                              Activate
                            </>
                          )}
                        </Button>
                        <Button 
                          variant="secondary" 
                          size="sm"
                          onClick={() => setEditingPlan(plan.id)}
                        >
                          Edit Plan
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
      </div>
    </Layout>
  );
}
